import React from "react";
import { useParams, Link } from "react-router-dom";
import Section from "../components/Section";
import projectsData from "../data/projects.json";

export default function ProjectCaseStudy() {
  const { slug } = useParams<{ slug: string }>();
  const project = projectsData.projects.find(
    (p: any) => (p.slug || p.name.toLowerCase().replace(/\s/g, "-")) === slug
  );

  if (!project) {
    return (
      <main className="min-h-screen">
        <Section title="Project not found" containerClassName="max-w-4xl">
          <p className="text-text-subtle">The requested case study does not exist.</p>
          <Link to="/projects" className="inline-flex mt-6 text-accent hover:text-primary transition">
            ← Back to Projects
          </Link>
        </Section>
      </main>
    );
  }

  return (
    <main className="min-h-screen">
      <Section title={project.name} eyebrow="case study" containerClassName="max-w-4xl">
        <Link
          to="/projects"
          className="font-mono text-xs text-text-subtle hover:text-primary mb-6 inline-flex items-center gap-2"
        >
          ← Back to Projects
        </Link>

        <article className="space-y-6 text-sm text-text-muted leading-relaxed">
          <section>
            <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-text-subtle">Overview</h2>
            <p className="mt-2">{project.overview || project.description}</p>
          </section>

          <section className="space-y-3 border border-border-subtle rounded-card p-4 bg-surface/70">
            <div>
              <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-text-subtle">Role & Responsibilities</h2>
              <p className="mt-2 text-text-muted">
                {project.role ||
                  "Hands-on contributor across discovery, build, and deployment phases."}
              </p>
            </div>
            {(project.responsibilities || []).length > 0 && (
              <ul className="list-disc pl-5 space-y-1">
                {project.responsibilities!.map((item: string) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </section>

          {Array.isArray(project.collaborators) && project.collaborators.length > 0 && (
            <section className="border border-border-subtle rounded-card p-4 bg-surface/70 space-y-2">
              <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-text-subtle">
                Collaborators
              </h2>
              <ul className="space-y-1 text-sm text-text-muted">
                {project.collaborators.map((collab: any, idx: number) => {
                  if (typeof collab === "string") {
                    return <li key={idx}>{collab}</li>;
                  }

                  const { name, role, url } = collab as {
                    name: string;
                    role?: string;
                    url?: string;
                  };

                  return (
                    <li key={idx}>
                      {url ? (
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="underline-offset-2 hover:underline text-primary"
                        >
                          {name}
                        </a>
                      ) : (
                        <span>{name}</span>
                      )}
                      {role && (
                        <span className="text-text-subtle text-xs ml-2">
                          {role}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <section>
            <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-text-subtle">Tech Stack</h2>
            <div className="mt-3 flex gap-2 flex-wrap">
              {(project.tech || []).map((t: string) => (
                <span
                  key={t}
                  className="text-[10px] font-mono px-3 py-1 border border-border-subtle rounded-pill text-primary/90"
                >
                  {t}
                </span>
              ))}
            </div>
          </section>

          {(project.outcomes || project.result) && (
            <section>
              <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-text-subtle">
                Key Outcomes
              </h2>
              {(project.outcomes || []).length > 0 ? (
                <ul className="mt-2 list-disc pl-5 space-y-1">
                  {project.outcomes!.map((item: string) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2">{project.result}</p>
              )}
            </section>
          )}

          <section className="grid gap-4 md:grid-cols-2">
            <div className="border border-border-subtle rounded-card p-4 bg-surface/60">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-text-subtle">Problem</p>
              <p className="mt-2">{project.problem || "Describe the problem this project solves."}</p>
            </div>
            <div className="border border-border-subtle rounded-card p-4 bg-surface/60">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-text-subtle">Solution</p>
              <p className="mt-2">
                {project.solution || "Describe how you built it (approach, architecture)."}
              </p>
            </div>
          </section>

          {project.slug === "evidence-wire" && (
            <section className="border border-border-subtle rounded-card p-4 bg-surface/70 text-sm text-text-subtle">
              Due to the nature of EvidenceWire, operational details remain intentionally high level. Reach out directly for a deeper technical walkthrough.
            </section>
          )}
        </article>
      </Section>
    </main>
  );
}
